<script setup lang="ts">
/**
 * ProfileSection — HOA Profile settings card.
 *
 * D-S1: NO brand color picker. Logo row only.
 * Numerics: yearFounded, homeCount use font-mono tabular-nums.
 * Short code regex: ^[a-z0-9-]{3,32}$
 */
import { ref, watch } from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import BaseCard from '@/components/ui/BaseCard.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useSettingsStore } from '@/stores/settings';

dayjs.extend(relativeTime);

const store = useSettingsStore();

// ── Local form state (mirrors store.profile) ──────────────────────────────────

const name = ref('');
const shortCode = ref('');
const city = ref('');
const state = ref('');
const yearFounded = ref<number | undefined>(undefined);
const homeCount = ref<number>(0);
const address = ref('');
const logoPreview = ref<string | null>(null);

// Validation errors
const errors = ref<Record<string, string>>({});

// Keep local state in sync when store loads
watch(
  () => store.profile,
  (p) => {
    if (!p) return;
    name.value = p.name;
    shortCode.value = p.shortCode;
    // portalUrl contains the short code; extract city/state from somewhere else
    // Note: city/state are on Hoa, not HoaSettings, so we approximate from portalUrl
    yearFounded.value = p.yearFounded ?? undefined;
    homeCount.value = p.homeCount;
    address.value = p.address ?? '';
    logoPreview.value = p.logoUrl ?? null;
  },
  { immediate: true },
);

function validate(): boolean {
  errors.value = {};
  if (!name.value.trim()) errors.value.name = 'Name is required.';
  if (!/^[a-z0-9-]{3,32}$/.test(shortCode.value))
    errors.value.shortCode = 'Short code must be 3–32 lowercase letters, digits, or dashes.';
  if (yearFounded.value !== undefined) {
    const y = Number(yearFounded.value);
    if (isNaN(y) || y < 1800 || y > 2100)
      errors.value.yearFounded = 'Year must be between 1800 and 2100.';
  }
  if (homeCount.value < 1 || homeCount.value > 99999)
    errors.value.homeCount = 'Homes must be between 1 and 99,999.';
  return Object.keys(errors.value).length === 0;
}

const saveError = ref<string | null>(null);
const saveSuccess = ref(false);

async function onSave() {
  if (!validate()) return;
  saveError.value = null;
  saveSuccess.value = false;
  try {
    await store.saveProfile({
      name: name.value,
      city: city.value,
      state: state.value,
      residentCount: homeCount.value,
      boardSeatCount: 7, // default — not editable in this form
      publicPortalSlug: shortCode.value,
      timezone: 'America/Chicago', // default — not editable in this form
      yearFounded: yearFounded.value ?? null,
      address: address.value || null,
    });
    saveSuccess.value = true;
  } catch (e) {
    saveError.value = e instanceof Error ? e.message : 'Save failed.';
  }
}

// Logo upload
const logoInput = ref<HTMLInputElement | null>(null);

function onUploadClick() {
  logoInput.value?.click();
}

async function onLogoChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  if (file.size > 2 * 1024 * 1024) {
    saveError.value = 'Logo must be under 2 MB.';
    return;
  }
  try {
    await store.uploadLogo(file);
    logoPreview.value = store.profile?.logoUrl ?? null;
    saveSuccess.value = true;
    saveError.value = null;
  } catch (e) {
    saveError.value = e instanceof Error ? e.message : 'Logo upload failed.';
  }
}
</script>

<template>
  <BaseCard id="profile" padding="none" data-testid="profile-section">
    <div class="p-4 sm:p-6 border-b border-border">
      <h3 class="text-base font-semibold text-fg">HOA profile</h3>
      <p class="text-sm text-muted mt-0.5">Public-facing information shown to residents and on reports.</p>
    </div>

    <div class="p-4 sm:p-6 space-y-5">
      <!-- Row 1: Name + Short code -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="space-y-1">
          <label class="block text-sm font-medium text-fg" for="profile-name">HOA name</label>
          <input
            id="profile-name"
            v-model="name"
            type="text"
            class="w-full h-9 px-3 rounded-md border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-navy-600"
            :class="errors.name ? 'border-red-400' : 'border-border bg-surface'"
            placeholder="Cypress Ridge HOA"
            data-testid="profile-name-input"
          >
          <p v-if="errors.name" class="text-xs text-red-600">{{ errors.name }}</p>
        </div>

        <div class="space-y-1">
          <label class="block text-sm font-medium text-fg" for="profile-shortcode">Short code</label>
          <input
            id="profile-shortcode"
            v-model="shortCode"
            type="text"
            class="w-full h-9 px-3 rounded-md border text-sm font-mono transition-colors focus:outline-none focus:ring-2 focus:ring-navy-600"
            :class="errors.shortCode ? 'border-red-400' : 'border-border bg-surface'"
            placeholder="cypressridge"
            data-testid="profile-shortcode-input"
          >
          <p class="text-xs text-muted">Used in subdomain &amp; URLs</p>
          <p v-if="errors.shortCode" class="text-xs text-red-600">{{ errors.shortCode }}</p>
        </div>
      </div>

      <!-- Row 2: Year Founded + # of homes -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="space-y-1">
          <label class="block text-sm font-medium text-fg" for="profile-year">Year founded</label>
          <input
            id="profile-year"
            v-model.number="yearFounded"
            type="number"
            min="1800"
            max="2100"
            class="w-full h-9 px-3 rounded-md border text-sm font-mono tabular-nums transition-colors focus:outline-none focus:ring-2 focus:ring-navy-600"
            :class="errors.yearFounded ? 'border-red-400' : 'border-border bg-surface'"
            placeholder="1998"
            data-testid="profile-year-input"
          >
          <p v-if="errors.yearFounded" class="text-xs text-red-600">{{ errors.yearFounded }}</p>
        </div>

        <div class="space-y-1">
          <label class="block text-sm font-medium text-fg" for="profile-homes"># of homes</label>
          <input
            id="profile-homes"
            v-model.number="homeCount"
            type="number"
            min="1"
            max="99999"
            class="w-full h-9 px-3 rounded-md border text-sm font-mono tabular-nums transition-colors focus:outline-none focus:ring-2 focus:ring-navy-600"
            :class="errors.homeCount ? 'border-red-400' : 'border-border bg-surface'"
            placeholder="412"
            data-testid="profile-homecount-input"
          >
          <p v-if="errors.homeCount" class="text-xs text-red-600">{{ errors.homeCount }}</p>
        </div>
      </div>

      <!-- Address -->
      <div class="space-y-1">
        <label class="block text-sm font-medium text-fg" for="profile-address">Address</label>
        <input
          id="profile-address"
          v-model="address"
          type="text"
          class="w-full h-9 px-3 rounded-md border border-border bg-surface text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-navy-600"
          placeholder="1100 Cypress Blvd, Austin, TX 78751"
          data-testid="profile-address-input"
        >
      </div>

      <!-- Logo row (D-S1: logo only, no brand-color picker) -->
      <div class="space-y-1">
        <span class="block text-sm font-medium text-fg">Logo</span>
        <div class="flex items-center gap-3">
          <!-- 48×48 preview -->
          <div
            class="w-12 h-12 rounded-md border border-border bg-canvas-soft overflow-hidden flex items-center justify-center shrink-0"
            data-testid="profile-logo-preview"
          >
            <img
              v-if="logoPreview"
              :src="logoPreview"
              alt="HOA logo"
              class="w-full h-full object-contain"
            >
            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              class="w-8 h-8 text-muted"
              aria-hidden="true"
            >
              <path d="M3 11.5L12 4l9 7.5" />
              <path d="M5 10v10h14V10" />
            </svg>
          </div>

          <BaseButton
            variant="secondary"
            size="sm"
            data-testid="profile-logo-upload-btn"
            @click="onUploadClick"
          >
            Upload new
          </BaseButton>

          <!-- Hidden file input -->
          <input
            ref="logoInput"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            class="sr-only"
            data-testid="profile-logo-file-input"
            @change="onLogoChange"
          >
        </div>
        <p class="text-xs text-muted">PNG, JPG, or WebP · max 2 MB</p>
      </div>

      <!-- Error / success banner -->
      <p v-if="saveError" class="text-sm text-red-600" role="alert">{{ saveError }}</p>
    </div>

    <div class="p-4 sm:p-6 border-t border-border flex items-center justify-between gap-3">
      <span class="text-sm text-muted">
        <template v-if="saveSuccess">Saved just now</template>
        <template v-else-if="store.updatedAt">
          Last saved {{ dayjs(store.updatedAt).fromNow() }}
        </template>
        <template v-else>Not yet saved</template>
      </span>
      <BaseButton
        variant="primary"
        size="sm"
        :loading="store.isSaving"
        data-testid="profile-save-btn"
        @click="onSave"
      >
        Save changes
      </BaseButton>
    </div>
  </BaseCard>
</template>
